import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { sortBy } from 'lodash';
import { TbSquareRoundedArrowLeft } from 'react-icons/tb';
import * as Styled from './style';
import { useAllComments } from '../../../../../services/comment.service';
import CommentResolvedState from '../ReportCellComment/CommentResolvedState';
import HighLightCellComment from './HighLightCellComment';
import commonStore from '../../../../../store/common';
import {
  COMMENT_REPORT_TYPES,
  COMMENT_CUSTOM_FUND_NAMES,
  COMMENTS_SIDEBAR_COLLAPSED_WIDTH,
  COMMENTS_SIDEBAR_EXPANDED_WIDTH
} from '../constants';
import { getReportType } from '../utils';
import type {
  SchemaCommentMessageResponseSchema,
  SchemaCommentThreadResponseSchema
} from '../../../../../api/generated/schema';
import type { NavigateToSectionFn } from '../../../../../consts/types';

type RefetchCommentsFn = () => Promise<unknown>;

interface ReportCommentsSidebarProps {
  id?: string;
  isMultiFunds?: boolean;
  isStaticTemplate?: boolean;
  onNavigateToSection: NavigateToSectionFn;
}

const ReportCommentsSidebar = ({
  id,
  isMultiFunds = false,
  onNavigateToSection,
  isStaticTemplate = false
}: ReportCommentsSidebarProps) => {
  const { workflowId, periodId } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const fundID = searchParams.get('fund_id');
  const isAIFM =
    searchParams.get('type') === 'AIF001' ||
    searchParams.get('reportType') === 'AIF001' ||
    fundID === 'AIF001';
  const currentReportType = getReportType(location?.pathname);

  const { data: threadsData = [], refetch } = useAllComments({
    queryOptions: {
      refetchOnMount: true
    },
    params: {
      workflow_id: workflowId,
      period_id: periodId
    }
  }) as { data?: SchemaCommentThreadResponseSchema[]; refetch: RefetchCommentsFn };

  let filteredThreadsData = threadsData.filter(thread => {
    // FCA UK AIFM
    if (isAIFM && currentReportType === COMMENT_REPORT_TYPES.FCA)
      return thread.fund_name === COMMENT_CUSTOM_FUND_NAMES.FCA_FUND_NAME;

    // ESMA AIFM
    if (isAIFM && currentReportType === COMMENT_REPORT_TYPES.ESMA)
      return thread.fund_name === COMMENT_CUSTOM_FUND_NAMES.ESMA_FUND_NAME;

    // FORM PF AIFM
    if (isAIFM && currentReportType === COMMENT_REPORT_TYPES.FORM_PF)
      return thread.fund_name === COMMENT_CUSTOM_FUND_NAMES.FORM_PF_FUND_NAME;

    // for multi view display all AIF funds
    if (isMultiFunds) return Boolean(thread.fund_id) && thread.report_type === currentReportType;

    return thread.fund_id === fundID && thread.report_type === currentReportType;
  });
  filteredThreadsData = sortBy(filteredThreadsData, 'created_at');

  const handleSidebarCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const renderComments = (comments?: SchemaCommentMessageResponseSchema[]) => {
    if (!Array.isArray(comments) || !comments.length) return null;

    return comments.map(comment => (
      <Styled.CommentWrap key={comment.id} id={`report-comments-sidebar-comment-${comment.id}`}>
        <div className="comment-header" id={`report-comments-sidebar-comment-header-${comment.id}`}>
          <p className="comment-author" id={`report-comments-sidebar-comment-author-${comment.id}`}>
            {comment.author_name}
          </p>
          <p id={`report-comments-sidebar-comment-created-at-${comment.id}`}>
            {moment.utc(comment.created_at).fromNow()}
          </p>
        </div>
        <span className="comment-text" id={`report-comments-sidebar-comment-text-${comment.id}`}>
          {comment.text}
        </span>
      </Styled.CommentWrap>
    ));
  };

  const renderThreads = () => {
    if (!Array.isArray(filteredThreadsData) || !filteredThreadsData.length) return null;

    return filteredThreadsData.map(threadDetail => {
      return Array.isArray(threadDetail.messages) && threadDetail.messages.length ? (
        <Styled.ThreadWrap
          key={threadDetail.id}
          id={`report-comments-sidebar-thread-${threadDetail.id}`}
        >
          <div
            className="thread-header"
            id={`report-comments-sidebar-thread-header-${threadDetail.id}`}
          >
            <p
              className="thread-fundname"
              id={`report-comments-sidebar-thread-fundname-${threadDetail.id}`}
            >
              {threadDetail.fund_name}
            </p>
            <div
              className="thread-actions"
              id={`report-comments-sidebar-thread-actions-${threadDetail.id}`}
            >
              <HighLightCellComment
                threadID={threadDetail.id}
                sectionID={threadDetail.table_name}
                onNavigateToSection={onNavigateToSection}
              />
              <CommentResolvedState
                threadID={threadDetail.id}
                isResolved={threadDetail.is_resolved}
                refetchComments={refetch}
              />
            </div>
          </div>
          <Styled.ThreadComments
            className="comments-list"
            id={`report-comments-sidebar-thread-comments-${threadDetail.id}`}
          >
            {renderComments(threadDetail.messages)}
          </Styled.ThreadComments>
        </Styled.ThreadWrap>
      ) : null;
    });
  };

  const sidebarWidth =
    isStaticTemplate || !threadsData?.length
      ? 0
      : isCollapsed
        ? COMMENTS_SIDEBAR_COLLAPSED_WIDTH
        : COMMENTS_SIDEBAR_EXPANDED_WIDTH;

  // Publish the sidebar's on-screen width so report containers can offset the
  // drill-down drawer correctly (0 when hidden, 55 collapsed, 270 expanded).
  useEffect(() => {
    commonStore.setCommentsSidebarWidth(sidebarWidth > 0 ? sidebarWidth + 20 : sidebarWidth);
    return () => commonStore.setCommentsSidebarWidth(0);
  }, [sidebarWidth]);

  if (!threadsData?.length || isStaticTemplate) return null;

  return (
    // @ts-ignore
    <Styled.StyledContainer id={id ?? 'report-comments-sidebar'} isCollapsed={isCollapsed}>
      {/* @ts-ignore */}
      <Styled.ListHeader id="report-comments-sidebar-header" isCollapsed={isCollapsed}>
        {!isCollapsed && <p id="report-comments-sidebar-title">Comments</p>}

        <button id="report-comments-sidebar-collapse-button" onClick={handleSidebarCollapse}>
          <TbSquareRoundedArrowLeft color="#DEE2E6" fontSize={20} />
        </button>
      </Styled.ListHeader>
      {!isCollapsed && <div id="report-comments-sidebar-threads">{renderThreads()}</div>}
    </Styled.StyledContainer>
  );
};

export default ReportCommentsSidebar;
